import React, { useEffect, useRef, useState } from "react";
import { AddToCartButton } from "./category";
import { fetchGet } from "common/xhr";
import { tokenList } from "common/util";
import { farIcon } from "h";
import TooltipWrapper from "cp/tooltip/Tooltip";

export {
  InteractivePrompt,
  PromptStrip,
};

const MAX_PROMPT_LENGTH = 300;

/**
 * InteractivePrompt — the slide-in AI Domain Idea Generator.
 *
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - initialPrompt: string — pre-fills the textarea when the panel opens
 *  - cart: { [domain]: price } — used to mark rows as "added"
 *  - loading: { [domain]: boolean } — used to track domain names that are
 *    being added or removed from the cart
 *  - sendAction: ({}) => void
 *  - qparams: object
 *  - examplePrompts: string[] — chip prompts shown above the textarea
 *
 * The panel reveals rows one-by-one (~180ms each) for a generative feel.
 */
function InteractivePrompt({
  open,
  onClose,
  initialPrompt = "",
  qparams,
  cart = {},
  loading = {},
  sendAction,
  examplePrompts = DEFAULT_EXAMPLE_PROMPTS,
}) {
  const [prompt, setPrompt] = useState(initialPrompt);
  const [status, setStatus] = useState("idle"); // idle | thinking | done
  const [results, setResults] = useState([]);
  const [errorMsg, setErrorMsg] = useState(null);
  const textareaRef = useRef(null);
  const domainsInCart = cart.items
    .filter(el => el.type == "domain")
    .map(el => el.name);

  // Reset on open
  useEffect(() => {
    if (open) {
      setPrompt(initialPrompt || "");
      setStatus("idle");
      setResults([]);
      setErrorMsg(null);
      const t = setTimeout(() => textareaRef.current?.focus(), 320);
      return () => clearTimeout(t);
    }
  }, [open, initialPrompt]);

  const onGenerate = async () => {
    if (!prompt.trim() || status === "thinking") return;
    setStatus("thinking");
    setResults([]);
    setErrorMsg(null);

    try {
      const items = await fetchSuggestions(prompt, qparams)
        .then(resp => resp.json());

      if (items?.succeeded === false || items?.error)
        throw items;
      if (!items?.length) throw new Error("No suggestions returned");

      // We expect item objects to include:
      //
      //   * price
      //   * regular_price
      //   * domain (domain name)
      //   * categoryName
      setResults(items);
    } catch (err) {
      let msg = "Suggestion service error";

      if (err.error)
        msg = err.error;

      setErrorMsg(msg);
    } finally {
      setStatus("done");
    }
  };

  return (
    <>
      <div
        className={"panel-scrim " + (open ? "open" : "")}
        onClick={onClose}
      />
      <aside
        className={"prompt-panel " + (open ? "open" : "")}
        aria-hidden={!open}
        aria-label="Domain name assistant"
      >
        <div className="panel-head">
          <div className="icon">
            <SparkleIcon size={20} />
          </div>
          <div className="titles">
            <h2>
              Domain name assistant
              <HoverLabsMark id="prompt_mark" />
            </h2>
            <div className="sub">
              Describe what you're building and we'll suggest relevant,
              available domain names.
            </div>
          </div>
          <button className="close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>
        <div className="panel-body">
          <div className="prompt-form">
            <div className="field-label">Tell us about your idea</div>
            <textarea
              ref={textareaRef}
              placeholder="e.g. A small-batch candle company that ships nationwide..."
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              maxLength={MAX_PROMPT_LENGTH}
            />
            <div
              className={
                "char-counter" +
                (prompt.length >= MAX_PROMPT_LENGTH - 30 ? " near-limit" : "")
              }
              aria-live="polite"
            >
              {prompt.length} / {MAX_PROMPT_LENGTH}
            </div>

            {errorMsg && <div className="api-error">{errorMsg}</div>}

            {!results.length && status !== "thinking" && (
              <div className="example-chips">
                {examplePrompts.map((p) => (
                  <button
                    type="button"
                    className="chip"
                    key={p}
                    onClick={() => setPrompt(p)}
                  >
                    {p}
                  </button>
                ))}
              </div>
            )}
            <div className="row-controls">
              <button
                className="generate-btn"
                onClick={onGenerate}
                disabled={!prompt.trim() || status === "thinking"}
              >
                <span className="spark">
                  <SparkleIcon size={14} color="white" />
                </span>
                Find your domain
              </button>
            </div>
          </div>

          {(status === "thinking" || results.length > 0) && (
            <div className="prompt-results">
              <div className="results-head">
                <span className="label">Suggested names</span>
                {status === "done" && (
                  <button className="regen" onClick={onGenerate}>
                    ↻ More ideas
                  </button>
                )}
              </div>
              {status === "thinking" && results.length === 0 && (
                <div className="thinking">
                  <span className="dots">
                    <span /><span /><span />
                  </span>
                  <span>
                    Brainstorming names for "
                    {prompt.slice(0, 40)}
                    {prompt.length > 40 ? "…" : ""}"
                  </span>
                </div>
              )}
              {results.map((item, idx) => {
                return (
                  <div
                    className="prompt-row"
                    key={item.domain + idx}
                    style={{ animationDelay: idx * 40 + "ms" }}
                  >
                    <div className="name-col">
                      <div className="dname">{item.domain}</div>
                      {item.why && <div className="why">{item.why}</div>}
                    </div>
                    <div className="price-col">
                      {item.regular_price != item.price && <span className="sale-tag">SALE</span>}
                      <span className="price">{item.price}</span>
                      {showStrikePrice(item) && <span className="strike">{item.regular_price}</span>}
                    </div>
                    <AddToCartButton
                      categoryName="ains"
                      domain={item.domain}
                      inCart={domainsInCart.includes(item.domain)}
                      loading={loading}
                      sendAction={sendAction}
                      prompt={prompt}
                    />
                  </div>
                );
              })}
              {status === "thinking" && results.length > 0 && (
                <div className="thinking" style={{ borderBottom: "none" }}>
                  <span className="dots">
                    <span /><span /><span />
                  </span>
                  <span>More on the way…</span>
                </div>
              )}
            </div>
          )}
        </div>
      </aside>
    </>
  );
}

/**
 * Compact strip to open interactive prompt sidebar.
 */
function PromptStrip({ onOpen, onDismiss }) {
  const sentinelRef = useRef(null);
  const [stuck, setStuck] = useState(false);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || typeof IntersectionObserver === "undefined") return;
    const obs = new IntersectionObserver(
      ([entry]) => setStuck(!entry.isIntersecting),
      { threshold: 0 }
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, []);

  return (
    <>
      <div ref={sentinelRef} className="prompt-strip-sentinel" aria-hidden="true" />
      <div className={tokenList({ "prompt-strip": true, stuck: stuck })}>
        <span className="spark">
          <SparkleIcon size={16} />
        </span>
        <div className="body">
          <HoverLabsMark id="strip_mark" />
          <br />
          Need help finding a domain name?
        </div>
        <button className="open" onClick={onOpen}>
          Get suggestions →
        </button>
        <button className="dismiss" onClick={onDismiss} aria-label="Dismiss">
          ×
        </button>
      </div>
    </>
  );
}

function SparkleIcon({ size = 14, color = "currentColor" }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M12 3L13.5 9L19 10.5L13.5 12L12 18L10.5 12L5 10.5L10.5 9L12 3Z"
        fill={color}
      />
      <path
        d="M19 16L19.7 18.3L22 19L19.7 19.7L19 22L18.3 19.7L16 19L18.3 18.3L19 16Z"
        fill={color}
        opacity="0.7"
      />
    </svg>
  );
}

function HoverLabsMark(props) {
  if (!props.id) {
    console.error('DOM ID required to use tooltip');
    return null;
  }

  return (
    <TooltipWrapper id={props.id} text="Hover Labs features are experimental.">
      <div className="hover-labs-mark">
        <div className="badge">Hover Labs</div>
        {farIcon("circle-info")}
      </div>
    </TooltipWrapper>
  );
}

async function fetchSuggestions(prompt, qparams) {
  const qs = new URLSearchParams();

  qs.set("prompt", prompt);
  qs.set("return_key", qparams.return_key);
  qs.set("q", qparams.q);

  return fetchGet("/api/suggestions?" + qs.toString());
}

function showStrikePrice(item) {
  return item.price && item.price != item.regular_price;
}

function randomPrompts(amount) {
  const shuffled = PROMPT_POOL.sort(el => Math.random() - 0.5);

  return shuffled.slice(0, amount);
}

/**
 * List of prompts stored outside of the codebase at:
 *
 * https://docs.google.com/spreadsheets/d/1BRUUaWE14KM_LgVC43peHO3O4nuYvRjXvXEFuaVsnSY/edit?gid=0#gid=0
 */
const PROMPT_POOL = [
  "AI-powered resume ghostwriting",
  "Subscription-box for rare spices",
  "Micro-sauna rental by the hour",
  "On-demand dog birthday catering",
  "Vertical herb farms for restaurants",
  "Sleep coaching for shift workers",
  "Luxury pet cremation & memorials",
  "Curated digital detox retreats",
  "Event catering service",
  "Virtual closet styling service",
  "Carbon-offset online gift shop",
  "Home and office decor service",
  "Noise-cancelling office booth rentals",
  "Personalized children's audiobooks",
  "Cleaning services",
  "Meal kit delivery service",
  "Corporate plant wall installations",
  "Cold plunge tub leasing service",
  "Memorial services business",
  "Blog about Vintage streetwear",
  "Freelance CFO for small businesses",
  "Sensory play spaces for toddlers",
  "Custom neon sign design studio",
  "Fermented beverage subscription club",
  "On-call handyman by subscription",
  "Executive burnout recovery programs",
  "Peer-to-peer surf lesson booking",
  "At home saloon service",
  "Mindful travel agency service",
  "Smart home setup for seniors",
  "B2B gift curation concierge",
  "On-demand sauna truck rental",
  "Micro-investment app",
  "Financial planning service",
  "Online flower boutique store",
  "Freelance HR for tech startups",
  "Branded podcast production studio",
  "Mobile IV drip therapy service",
  "Artisan charcuterie catering service",
  "Indoor cycling studio on wheels",
  "ESG reporting tool for SMBs",
  "Virtual book club platform with AI",
  "Prenatal yoga on-demand streaming",
  "Custom fragrance formulation bar",
  "Sneaker cleaning & restoration shop",
  "Trauma-informed workplace coaching",
  "Backyard wedding venue marketplace",
  "Luxury airport transfer subscription",
  "Pediatric telehealth concierge care",
  "Heritage recipe preservation service",
];

const DEFAULT_EXAMPLE_PROMPTS = randomPrompts(3);
